import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../../lib/supabase";

interface ProductItem {
  id: string;
  name: string;
  price?: string;
  imageUrl?: string;
  companyName?: string;
}

interface SidebarProductScrollerProps {
  isKr: boolean;
}

const SidebarProductScroller = ({ isKr }: SidebarProductScrollerProps) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const tickerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<number | null>(null);
  const posRef = useRef(0);

  useEffect(() => {
    const fetchProducts = async () => {
      const { data } = await supabase
        .from("san_pham_han_quoc")
        .select("id, name, name_kr, price, images, company_name")
        .order("created_at", { ascending: false })
        .limit(20);

      if (data) {
        setProducts(
          data.map((p) => ({
            id: p.id,
            name: isKr ? (p.name_kr || p.name) : p.name,
            price: p.price,
            imageUrl: Array.isArray(p.images) && p.images.length > 0 ? p.images[0] : undefined,
            companyName: p.company_name,
          }))
        );
      }
      setLoading(false);
    };
    fetchProducts();
  }, [isKr]);

  useEffect(() => {
    const el = tickerRef.current;
    if (!el || loading || products.length === 0) return;
    const SPEED = 0.4;
    const animate = () => {
      if (!isPaused && el) {
        posRef.current += SPEED;
        const halfH = el.scrollHeight / 2;
        if (posRef.current >= halfH) posRef.current = 0;
        el.style.transform = `translateY(-${posRef.current}px)`;
      }
      animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [isPaused, loading, products.length]);

  const doubled = [...products, ...products];

  return (
    <div className="h-full flex flex-col bg-white/80 backdrop-blur-sm border-r border-gray-100/80">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#004A99] to-[#0066cc] px-2 py-2.5 text-center flex-shrink-0">
        <p className="text-white text-[10px] font-bold tracking-wider uppercase whitespace-nowrap">
          {isKr ? "신상품" : "SẢN PHẨM MỚI"}
        </p>
        <p className="text-white/60 text-[9px] mt-0.5">
          {products.length} {isKr ? "개" : "sản phẩm"}
        </p>
      </div>

      {/* Scrolling products */}
      <div
        className="flex-1 overflow-hidden"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {loading ? (
          <div className="flex flex-col gap-2 p-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="w-full aspect-square bg-gray-100 rounded-lg mb-1.5" />
                <div className="h-2.5 bg-gray-100 rounded w-3/4 mx-auto mb-1" />
                <div className="h-2 bg-gray-100 rounded w-1/2 mx-auto" />
              </div>
            ))}
          </div>
        ) : (
          <div ref={tickerRef} className="will-change-transform">
            {doubled.map((product, idx) => (
              <div
                key={`${product.id}-${idx}`}
                className="flex flex-col items-center px-2 py-3 cursor-pointer hover:bg-[#004A99]/5 transition-colors group border-b border-gray-50"
                onClick={() => navigate(`/product/${product.id}`)}
              >
                {/* Product image */}
                <div className="w-full aspect-square rounded-lg overflow-hidden mb-2 bg-gray-50 border border-gray-100">
                  {product.imageUrl ? (
                    <img
                      src={product.imageUrl}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#004A99]/10 to-[#00AEEF]/10">
                      <i className="ri-box-3-line text-[#004A99]/30 text-2xl" />
                    </div>
                  )}
                </div>

                {/* Product name */}
                <p className="text-[11px] font-semibold text-gray-700 text-center line-clamp-2 leading-tight group-hover:text-[#004A99] transition-colors w-full">
                  {product.name}
                </p>

                {/* Price */}
                {product.price && (
                  <p className="text-[10px] text-[#004A99] font-bold mt-1 whitespace-nowrap">
                    {product.price}
                  </p>
                )}

                {/* Company */}
                {product.companyName && (
                  <p className="text-[9px] text-gray-400 mt-0.5 text-center line-clamp-1 w-full">
                    {product.companyName}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SidebarProductScroller;
